import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Registering only the languages QuickAI answers realistically need keeps this
// out of the "bundle all ~200 Prism grammars" trap that the default `Prism`
// import from this package falls into.
SyntaxHighlighter.registerLanguage('javascript', jsx);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('tsx', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('html', markup);
SyntaxHighlighter.registerLanguage('xml', markup);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('yaml', yaml);
import {
  FiZap,
  FiPlus,
  FiMessageSquare,
  FiTrash2,
  FiCopy,
  FiCheck,
  FiSend,
  FiSquare,
  FiMenu,
  FiX,
  FiAlertCircle,
  FiUsers,
  FiUser,
  FiEye,
  FiSearch,
  FiSettings,
  FiPaperclip,
  FiFile,
  FiFileText,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { quickAIService } from '../services/apiService';
import { useT } from '../utils/i18n';
import { useAuthStore } from '../utils/store';
import { useCustomPrompts, PromptManager, PromptPicker } from '../components/Common/CustomPrompts';

const ADMIN_HISTORY_ROLES = ['admin', 'superadmin', 'manager'];

// Attachments: any file — pictures, PDFs, Word, Excel, PowerPoint, text… —
// up to 5 per message (the server checks the same limits).
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const isImageFile = (file) => /^image\/(jpeg|png|webp|gif)$/.test(file.type);
const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const TEXT_KINDS = new Set(['pdf', 'docx', 'doc', 'sheet', 'slides', 'text']);
// Before the server has looked at a file: a document by its name, or a plain file.
const localKind = (file) => (/\.(pdf|docx?|xlsx|pptx|txt|md|csv|tsv|json|html?|xml|srt|vtt)$/i.test(file.name) || file.type.startsWith('text/') ? 'text' : 'other');

// ─── An attached file: a picture's thumbnail, or a chip with its name ────────
// `url` opens it (a local preview before it's sent, the server's copy after).
// onRemove: shown while it's still waiting to be sent.
const AttachmentChip = ({ name, size, image, kind, url, onRemove, removeLabel }) => {
  const Icon = TEXT_KINDS.has(kind) ? FiFileText : FiFile;
  const remove = onRemove && (
    <button
      type="button"
      onClick={onRemove}
      title={removeLabel}
      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-gray-900"
    >
      <FiX size={11} />
    </button>
  );
  if (image && url) {
    return (
      <div className="relative">
        <a href={url} target="_blank" rel="noopener noreferrer" title={name}>
          <img src={url} alt={name} className="w-16 h-16 rounded-lg object-cover border border-gray-200 bg-gray-50" />
        </a>
        {remove}
      </div>
    );
  }
  const body = (
    <>
      <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
        <Icon size={15} />
      </span>
      <span className="min-w-0 text-left">
        <span className="block text-xs font-medium text-gray-700 truncate max-w-[160px]">{name}</span>
        {size ? <span className="block text-[10px] text-gray-400">{formatSize(size)}</span> : null}
      </span>
    </>
  );
  return (
    <div className="relative">
      {url && !onRemove ? (
        <a href={url} target="_blank" rel="noopener noreferrer" title={name} className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50">
          {body}
        </a>
      ) : (
        <div title={name} className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border border-gray-200 bg-white">{body}</div>
      )}
      {remove}
    </div>
  );
};

/** A message as the page keeps it, from the server's copy (attachments included). */
const toPageMessage = (m) => ({ role: m.role, content: m.content || '', ...(m.attachments?.length ? { attachments: m.attachments } : {}) });

// ─── Code block renderer — every fenced code block gets its own copy button,
// same as ChatGPT's per-block "Copy code" affordance. ──────────────────────
const CodeBlock = ({ inline, className, children }) => {
  const [copied, setCopied] = useState(false);
  const text = String(children).replace(/\n$/, '');

  if (inline) {
    return <code className="px-1.5 py-0.5 rounded bg-gray-100 text-[0.85em] font-mono text-gray-800">{text}</code>;
  }

  const lang = /language-(\w+)/.exec(className || '')?.[1] || 'text';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard access denied — silently ignore, button just won't confirm
    }
  };

  return (
    <div className="my-2 rounded-lg overflow-hidden border border-gray-700/50 text-sm">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1f2937] text-[#d1d5db] text-[11px] font-mono">
        <span>{lang}</span>
        <button onClick={handleCopy} className="flex items-center gap-1 hover:text-white transition-colors">
          {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={lang}
        style={oneDark}
        customStyle={{ margin: 0, padding: '0.75rem', fontSize: '0.8rem', background: '#1e1e2e' }}
      >
        {text}
      </SyntaxHighlighter>
    </div>
  );
};

const MARKDOWN_COMPONENTS = {
  code: CodeBlock,
  table: (props) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border-collapse text-sm" {...props} />
    </div>
  ),
  th: (props) => <th className="border border-gray-300 bg-gray-50 px-3 py-1.5 text-left font-semibold" {...props} />,
  td: (props) => <td className="border border-gray-300 px-3 py-1.5 align-top" {...props} />,
  a: (props) => <a className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
};

// ─── One chat bubble ─────────────────────────────────────────────────────────
const MessageBubble = ({ role, content, streaming, attachments, threadId }) => {
  const [copied, setCopied] = useState(false);
  const isUser = role === 'user';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  if (isUser) {
    return (
      <div className="flex flex-col items-end gap-1.5 px-4 py-2">
        {attachments?.length > 0 && (
          <div className="flex flex-wrap justify-end gap-2 max-w-[85%] sm:max-w-[75%]">
            {attachments.map((a, i) => (
              <AttachmentChip
                key={a.id || `${a.name}-${i}`}
                name={a.name}
                size={a.size}
                kind={a.kind}
                image={a.kind === 'image'}
                // Just sent: the local preview. From the server: its copy (once the chat has an id).
                url={a.localUrl || (a.id && threadId ? quickAIService.fileUrl(threadId, a.id) : null)}
              />
            ))}
          </div>
        )}
        {content && (
          <div className="max-w-[85%] sm:max-w-[75%] bg-primary-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
            {content}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group px-4 py-2">
      <div className="max-w-[95%] sm:max-w-[85%]">
        <div className="prose prose-sm max-w-none text-[15px] leading-relaxed text-gray-800 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-[15px] [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-semibold">
          {content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
              {content}
            </ReactMarkdown>
          ) : streaming ? (
            <span className="inline-flex items-center gap-1 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : null}
        </div>
        {content && !streaming && (
          <button
            onClick={handleCopy}
            title="Copy response"
            className="mt-1 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            {copied ? <FiCheck size={13} className="text-green-500" /> : <FiCopy size={13} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

export const QuickAI = () => {
  const t = useT();
  const { user } = useAuthStore();
  const canViewTeamHistory = ADMIN_HISTORY_ROLES.includes(user?.role);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Files waiting to go with the next message: { id, file, previewUrl (pictures) }.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Admin/manager "team history" — a read-only view of every in-scope user's
  // threads (access rules match the rest of the app's history views: admin
  // sees everyone, manager sees other managers+editors, editors never reach
  // this at all — enforced server-side, this is just the UI for it).
  const [sidebarTab, setSidebarTab] = useState('mine'); // 'mine' | 'team'
  const [teamThreads, setTeamThreads] = useState([]);
  const [teamThreadsLoading, setTeamThreadsLoading] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [viewingOwner, setViewingOwner] = useState(null); // { name, email } when the loaded thread belongs to someone else

  // The user's saved prompts. Picking one puts its text in the message box,
  // where it can be changed for this message or added to.
  const { prompts, limits: promptLimits, reload: reloadPrompts } = useCustomPrompts('quickAI');
  const [activePromptId, setActivePromptId] = useState(null);
  const [promptManagerOpen, setPromptManagerOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const activeThreadIdRef = useRef(null); // avoids stale closures inside stream callbacks

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  // ── Load thread list on mount ─────────────────────────────────────────────
  const refreshThreads = useCallback(async () => {
    try {
      const data = await quickAIService.listThreads();
      setThreads(data || []);
    } catch {
      // Silent — the chat itself still works without a thread list
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshThreads();
  }, [refreshThreads]);

  // ── Team history (admin/manager only) — refetched on tab switch and,
  // debounced, as the name search changes. ──────────────────────────────────
  const refreshTeamThreads = useCallback(async (userName) => {
    setTeamThreadsLoading(true);
    try {
      const result = await quickAIService.listThreadsForAdmin({ userName: userName || undefined });
      setTeamThreads(result?.data || []);
    } catch {
      toast.error('Could not load team history.');
    } finally {
      setTeamThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewTeamHistory || sidebarTab !== 'team') return;
    const timer = setTimeout(() => refreshTeamThreads(teamSearch), teamSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [canViewTeamHistory, sidebarTab, teamSearch, refreshTeamThreads]);

  // ── Follow the reply as it streams in ─────────────────────────────────────
  // Only while the user is at the bottom: scrolling up to read stops it. And
  // an instant jump, not a smooth scroll: a smooth scroll restarted 25 times a
  // second never caught up, so the visible text appeared to stop mid-answer
  // while the rest was arriving below the fold.
  const followLatestRef = useRef(true);
  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    followLatestRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  useEffect(() => {
    if (followLatestRef.current) messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  // ── Auto-grow textarea, capped ─────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  // ── Attachments ───────────────────────────────────────────────────────────
  // Previews of pictures are object URLs, used by the waiting chips and by the
  // sent message's bubble alike — all released when the page closes.
  const previewUrlsRef = useRef([]);
  useEffect(() => () => previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const accepted = [];
    for (const file of incoming) {
      if (!file.size) {
        toast.error(t('quickAI.emptyFile', { name: file.name }));
      } else if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(t('quickAI.fileTooLarge', { name: file.name }));
      } else {
        let previewUrl = null;
        if (isImageFile(file)) {
          previewUrl = URL.createObjectURL(file);
          previewUrlsRef.current.push(previewUrl);
        }
        accepted.push({ id: `${file.name}-${file.size}-${Math.random()}`, file, previewUrl });
      }
    }
    if (pendingFiles.length + accepted.length > MAX_ATTACHMENTS) toast.error(t('quickAI.tooManyFiles', { max: MAX_ATTACHMENTS }));
    setPendingFiles((prev) => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS));
  };

  const removePendingFile = (id) => setPendingFiles((prev) => prev.filter((p) => p.id !== id));

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!viewingOwner && !isStreaming) addFiles(e.dataTransfer?.files);
  };

  // Put the chosen prompt's text at the top of the message box; text typed
  // under a previously chosen prompt is kept ("No prompt" just removes it).
  const choosePrompt = (id) => {
    const previous = prompts.find((p) => p.id === activePromptId)?.text;
    const next = prompts.find((p) => p.id === id);
    setInput((current) => {
      const rest = (previous && current.startsWith(previous) ? current.slice(previous.length) : current).replace(/^\s+/, '');
      if (!next) return rest;
      return rest ? `${next.text}\n\n${rest}` : next.text;
    });
    setActivePromptId(next ? next.id : null);
    textareaRef.current?.focus();
  };

  const handleNewChat = () => {
    if (isStreaming) return;
    setSidebarTab('mine');
    setViewingOwner(null);
    setActiveThreadId(null);
    setMessages([]);
    setPendingFiles([]);
    setError('');
    textareaRef.current?.focus();
  };

  const handleSelectThread = async (id) => {
    if ((id === activeThreadId && !viewingOwner) || isStreaming) return;
    setMobileSidebarOpen(false);
    try {
      const thread = await quickAIService.getThread(id);
      setMessages((thread.messages || []).map(toPageMessage));
      setActiveThreadId(id);
      setViewingOwner(null);
      setError('');
    } catch {
      toast.error('Could not load this chat.');
    }
  };

  // Read-only: loads someone else's thread via the admin/manager-scoped
  // endpoint. No delete, no composer — see viewingOwner banner + disabled
  // composer below.
  const handleSelectTeamThread = async (id) => {
    if (id === activeThreadId || isStreaming) return;
    setMobileSidebarOpen(false);
    try {
      const thread = await quickAIService.getThreadForAdmin(id);
      setMessages((thread.messages || []).map(toPageMessage));
      setActiveThreadId(id);
      setViewingOwner(thread.userId ? { name: thread.userId.name, email: thread.userId.email } : null);
      setError('');
    } catch {
      toast.error('Could not load this chat.');
    }
  };

  const handleDeleteThread = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('quickAI.deleteConfirm'))) return;
    try {
      await quickAIService.deleteThread(id);
      setThreads((prev) => prev.filter((th) => th._id !== id));
      if (id === activeThreadId) {
        setActiveThreadId(null);
        setMessages([]);
      }
    } catch {
      toast.error('Could not delete this chat.');
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
  };

  const handleSend = async () => {
    const text = input.trim();
    const sentFiles = pendingFiles;
    if ((!text && !sentFiles.length) || isStreaming || viewingOwner) return; // read-only when viewing someone else's chat

    // Shown in the sent message at once, from the local copies.
    const sentAttachments = sentFiles.map((p) => ({
      name: p.file.name,
      size: p.file.size,
      kind: p.previewUrl ? 'image' : localKind(p.file),
      localUrl: p.previewUrl,
    }));

    setInput('');
    setPendingFiles([]);
    setActivePromptId(null);
    setError('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text, ...(sentAttachments.length ? { attachments: sentAttachments } : {}) },
      { role: 'assistant', content: '' },
    ]);
    setIsStreaming(true);
    followLatestRef.current = true; // the user's own new message — show it

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const threadIdAtSend = activeThreadIdRef.current;

    // Network chunks can arrive in fast, uneven bursts (several tokens within
    // the same few milliseconds). Calling setMessages on every single one
    // makes each update wait on a full ReactMarkdown re-parse + re-render of
    // the whole accumulated message — on a burst, or once the message gets
    // long, that work can't keep up with the arrival rate, and the result
    // *looks* like the text appeared all at once instead of typing in. This
    // decouples "receive" (instant, just appends to a ref) from "paint"
    // (throttled to a fixed cadence) so the DOM only re-parses at a
    // sustainable rate, smooth regardless of how bursty delivery is.
    //
    // Deliberately setTimeout, not requestAnimationFrame: rAF is tied to the
    // browser's paint/compositor cycle, which can be throttled or delayed
    // depending on tab/rendering state — confirmed live in headless-browser
    // testing, where rAF-scheduled flushes never fired during the stream at
    // all, so the whole reply appeared in one jump at the very end (text
    // length measured 0 for 4+ seconds, then jumped straight to the final
    // length in a single frame). setTimeout fires on a plain timer,
    // independent of paint state, so it can't have that failure mode.
    const FLUSH_INTERVAL_MS = 40;
    let pendingText = '';
    let flushTimer = null;
    const flushPendingText = () => {
      flushTimer = null;
      if (!pendingText) return;
      const toAppend = pendingText;
      pendingText = '';
      setMessages((prev) => {
        const next = [...prev];
        const lastIdx = next.length - 1;
        next[lastIdx] = { ...next[lastIdx], content: next[lastIdx].content + toAppend };
        return next;
      });
    };
    let received = '';
    const appendToLastAssistant = (delta) => {
      received += delta;
      pendingText += delta;
      if (!flushTimer) {
        flushTimer = setTimeout(flushPendingText, FLUSH_INTERVAL_MS);
      }
    };

    // The reply as the server saved it — shown when what arrived here is
    // shorter than what was sent (lost on the way) or the stream was cut.
    const showSavedReply = async () => {
      const threadId = activeThreadIdRef.current;
      if (!threadId) return false;
      try {
        const thread = await quickAIService.getThread(threadId);
        if (activeThreadIdRef.current !== threadId) return false;
        const saved = (thread.messages || []).map(toPageMessage);
        if (saved.at(-1)?.role !== 'assistant') return false;
        setMessages(saved);
        return true;
      } catch {
        return false;
      }
    };

    try {
      await quickAIService.streamMessage(
        { threadId: threadIdAtSend, message: text, files: sentFiles.map((p) => p.file) },
        {
          signal: controller.signal,
          onThread: ({ threadId }) => {
            setActiveThreadId(threadId);
            activeThreadIdRef.current = threadId;
          },
          onChunk: (delta) => appendToLastAssistant(delta),
          onDone: ({ length } = {}) => {
            clearTimeout(flushTimer);
            flushPendingText(); // guarantee the tail end isn't stranded in an unflushed timer
            if (length !== undefined && length !== received.length) showSavedReply();
            refreshThreads();
          },
          onError: async (message, { dropped } = {}) => {
            clearTimeout(flushTimer);
            flushPendingText();
            // Cut off on the way: the server still has the whole reply if it finished.
            if (dropped && (await showSavedReply())) return;
            if (!received) {
              // Nothing came back: give the message — and its files — back to edit or resend.
              setMessages((prev) => prev.slice(0, -2));
              setInput((current) => current || text);
              setPendingFiles((current) => (current.length ? current : sentFiles));
            }
            setError(message || 'Something went wrong. Please try again.');
          },
        }
      );
    } catch (e) {
      clearTimeout(flushTimer);
      flushPendingText();
      if (e?.name !== 'AbortError') {
        setError(e.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatThreadDate = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // ── Thread list (shared between desktop rail + mobile drawer) ─────────────
  // An element, not a component: defined as a component inside this one, the
  // whole list was unmounted and rebuilt on every render — 25 times a second
  // while a reply streams in.
  const threadList = (
    <>
      <button
        onClick={handleNewChat}
        disabled={isStreaming}
        className="flex items-center gap-2 mx-2 mt-2 mb-1 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50 transition-colors"
      >
        <FiPlus size={15} />
        {t('quickAI.newChat')}
      </button>

      {/* My Chats / Team tab toggle — team tab only exists for admin/manager */}
      {canViewTeamHistory && (
        <div className="flex mx-2 mt-1 mb-1 p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
          <button
            onClick={() => setSidebarTab('mine')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-colors ${
              sidebarTab === 'mine' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUser size={12} />
            {t('quickAI.myChats')}
          </button>
          <button
            onClick={() => setSidebarTab('team')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-colors ${
              sidebarTab === 'team' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUsers size={12} />
            {t('quickAI.team')}
          </button>
        </div>
      )}

      {sidebarTab === 'team' && canViewTeamHistory ? (
        <>
          <div className="relative mx-2 mb-1">
            <FiSearch size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              placeholder={t('quickAI.searchUser')}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
            {teamThreadsLoading ? (
              <p className="px-3 py-2 text-xs text-gray-400">{t('quickAI.loadingChats')}</p>
            ) : teamThreads.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">{t('quickAI.noTeamChats')}</p>
            ) : (
              teamThreads.map((th) => (
                <button
                  key={th._id}
                  onClick={() => handleSelectTeamThread(th._id)}
                  className={`w-full flex flex-col gap-0.5 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
                    th._id === activeThreadId
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FiMessageSquare size={13} className="flex-shrink-0 text-gray-400" />
                    <span className="flex-1 min-w-0 truncate">{th.title || 'New chat'}</span>
                    <span className="flex-shrink-0 text-[10px] text-gray-400">{formatThreadDate(th.updatedAt)}</span>
                  </span>
                  <span className="pl-5 text-[11px] text-gray-400 truncate">{th.userId?.name || th.userId?.email || 'Unknown user'}</span>
                </button>
              ))
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
          {threadsLoading ? (
            <p className="px-3 py-2 text-xs text-gray-400">{t('quickAI.loadingChats')}</p>
          ) : threads.length === 0 ? (
            <p className="px-3 py-4 text-xs text-gray-400 text-center">{t('quickAI.noChats')}</p>
          ) : (
            threads.map((th) => (
              <button
                key={th._id}
                onClick={() => handleSelectThread(th._id)}
                className={`group/thread w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${
                  th._id === activeThreadId && !viewingOwner
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FiMessageSquare size={13} className="flex-shrink-0 text-gray-400" />
                <span className="flex-1 min-w-0 truncate">{th.title || 'New chat'}</span>
                <span className="flex-shrink-0 text-[10px] text-gray-400 group-hover/thread:hidden">
                  {formatThreadDate(th.updatedAt)}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => handleDeleteThread(th._id, e)}
                  className="hidden group-hover/thread:flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-danger-600 hover:bg-danger-50 flex-shrink-0"
                  title="Delete chat"
                >
                  <FiTrash2 size={12} />
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)] min-h-[480px] -mx-3 sm:-mx-4 md:-mx-6 -mb-3 sm:-mb-4 md:-mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 pb-3 flex-shrink-0">
        <button
          onClick={() => setMobileSidebarOpen((v) => !v)}
          className="md:hidden text-gray-500 hover:text-gray-700"
          aria-label="Toggle chat list"
        >
          <FiMenu size={18} />
        </button>
        <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiZap size={17} className="text-primary-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{t('quickAI.title')}</h1>
          <p className="text-xs text-gray-500 leading-tight hidden sm:block">{t('quickAI.desc')}</p>
        </div>
      </div>

      {/* Body: thread rail + chat panel */}
      <div className="flex-1 flex border-t border-gray-200 overflow-hidden bg-white">
        {/* Desktop thread rail */}
        <div className="hidden md:flex md:flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50/50">
          {threadList}
        </div>

        {/* Mobile drawer */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-xl">
              <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">{t('quickAI.chats')}</span>
                <button onClick={() => setMobileSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <FiX size={18} />
                </button>
              </div>
              {threadList}
            </div>
          </div>
        )}

        {/* Chat column — also the drop target for attachments */}
        <div
          className="relative flex-1 flex flex-col min-w-0"
          onDragOver={(e) => { e.preventDefault(); if (!viewingOwner && !isStreaming) setDragging(true); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
          onDrop={handleDrop}
        >
          {dragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-50/90 border-2 border-dashed border-primary-400 pointer-events-none">
              <p className="text-sm font-medium text-primary-700">{t('quickAI.dropHere', { max: MAX_ATTACHMENTS })}</p>
            </div>
          )}
          {viewingOwner && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex-shrink-0">
              <FiEye size={13} className="flex-shrink-0" />
              <span>
                {t('quickAI.viewingAs')} <strong>{viewingOwner.name || viewingOwner.email}</strong> — {t('quickAI.readOnly')}
              </span>
            </div>
          )}
          <div className="flex-1 overflow-y-auto py-3" onScroll={handleMessagesScroll}>
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-2">
                <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mb-1">
                  <FiZap size={22} className="text-primary-600" />
                </div>
                <p className="text-gray-700 font-semibold">{t('quickAI.emptyTitle')}</p>
                <p className="text-sm text-gray-400 max-w-sm">{t('quickAI.emptyDesc')}</p>
              </div>
            ) : (
              <>
                {messages.map((m, idx) => (
                  <MessageBubble
                    key={idx}
                    role={m.role}
                    content={m.content}
                    attachments={m.attachments}
                    threadId={activeThreadId}
                    streaming={isStreaming && idx === messages.length - 1 && m.role === 'assistant'}
                  />
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {error && (
            <div className="mx-4 mb-2 flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5 flex-shrink-0">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError('')} className="text-danger-400 hover:text-danger-600"><FiX size={12} /></button>
            </div>
          )}

          {/* Composer — hidden entirely while viewing someone else's read-only chat */}
          {!viewingOwner && (
            <div className="flex-shrink-0 border-t border-gray-200 px-3 sm:px-4 py-3">
              {/* Saved prompts: picking one puts its text in the message box to use or refine */}
              <div className="flex items-center gap-2 max-w-3xl mx-auto mb-2 text-xs text-gray-500">
                <label htmlFor="quick-ai-prompt">{t('prompts.label')}</label>
                <PromptPicker
                  id="quick-ai-prompt"
                  prompts={prompts}
                  value={activePromptId}
                  onChange={choosePrompt}
                  disabled={isStreaming}
                  allowNone
                  className="w-[180px] sm:w-[240px]"
                />
                <button
                  type="button"
                  onClick={() => setPromptManagerOpen(true)}
                  className="inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
                >
                  <FiSettings size={11} />
                  {t('prompts.manageButton')}
                </button>
              </div>
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 max-w-3xl mx-auto mb-2 pt-1.5">
                  {pendingFiles.map((p) => (
                    <AttachmentChip
                      key={p.id}
                      name={p.file.name}
                      size={p.file.size}
                      kind={p.previewUrl ? 'image' : localKind(p.file)}
                      image={!!p.previewUrl}
                      url={p.previewUrl}
                      onRemove={() => removePendingFile(p.id)}
                      removeLabel={t('quickAI.removeFile')}
                    />
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2 max-w-3xl mx-auto">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isStreaming || pendingFiles.length >= MAX_ATTACHMENTS}
                  title={t('quickAI.attach', { max: MAX_ATTACHMENTS })}
                  className="flex-shrink-0 w-10 h-10 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  <FiPaperclip size={16} />
                </button>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder={t('quickAI.inputPlaceholder')}
                  rows={1}
                  className="flex-1 resize-none rounded-2xl border border-gray-300 px-4 py-2.5 text-[15px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-[200px]"
                />
                {isStreaming ? (
                  <button
                    onClick={handleStop}
                    title={t('quickAI.stop')}
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-900 text-white flex items-center justify-center transition-colors"
                  >
                    <FiSquare size={14} />
                  </button>
                ) : (
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() && !pendingFiles.length}
                    title={t('quickAI.send')}
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                  >
                    <FiSend size={15} />
                  </button>
                )}
              </div>
              <p className="text-center text-[11px] text-gray-400 mt-2 hidden sm:block">{t('quickAI.disclaimer')}</p>
            </div>
          )}
        </div>
      </div>

      {promptManagerOpen && (
        <PromptManager
          scope="quickAI"
          title={t('prompts.managerTitle', { place: t('quickAI.title') })}
          prompts={prompts}
          limits={promptLimits}
          onChanged={reloadPrompts}
          onClose={() => setPromptManagerOpen(false)}
        />
      )}
    </div>
  );
};

export default QuickAI;
